/**
 * CustomerProposalView — Main customer proposal layout
 *
 * Single-column, scrollable, dark-themed proposal page.
 * Composes: ProposalHero, ProposalSystemOverview, ProposalTerms, ProposalSavingsTimeline.
 *
 * Used on shared pages when allowed_views includes 'proposal'.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Box, Container, Typography, Alert, Divider, IconButton } from '@mui/material';
import { TrendingFlat as ArrowIcon } from '@mui/icons-material';
import {
  Download as DownloadIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { extractAllProposalData, formatCurrency, formatPercent } from './extractProposalData';
import ProposalHero from './ProposalHero';
import ProposalDispatch from './ProposalDispatch';
import ProposalPerformance from './ProposalPerformance';
import ProposalSystemOverview from './ProposalSystemOverview';
import ProposalTerms from './ProposalTerms';
import ProposalAssumptions from './ProposalAssumptions';
import ProposalCashflowTable from './ProposalCashflowTable';
import ProposalMonthlyPerformance from './ProposalMonthlyPerformance';
import ProposalSavingsTimeline from './ProposalSavingsTimeline';

export default function CustomerProposalView({ scenarios, scenarioNames, customerName, projectName, dealModeFilter, createdAt, recommendedIndex, recommendationText }) {
  const [activeTab, setActiveTab] = useState(0); // number = scenario index, 'compare' = compare mode
  const [preferredDealMode, setPreferredDealMode] = useState(null); // shared across tabs
  const [rateOverrides, setRateOverrides] = useState(null); // customer-adjusted escalation rates

  const isCompare = activeTab === 'compare';

  // Extract and validate proposal data for each scenario (both deal modes)
  const proposalEntries = useMemo(() => {
    return scenarios.map((s, i) => {
      try {
        const all = extractAllProposalData(s);
        const allModes = [all.esa && 'esa', all.capex && 'capex'].filter(Boolean);
        // Apply deal mode filter from share config (if set)
        const modes = dealModeFilter && dealModeFilter !== 'both'
          ? allModes.filter(m => m === dealModeFilter)
          : allModes;
        if (modes.length === 0) throw new Error('No deal analysis configured');
        return { modes, esa: all.esa, capex: all.capex, name: scenarioNames[i], error: null };
      } catch (err) {
        return { modes: [], esa: null, capex: null, name: scenarioNames[i], error: err.message };
      }
    });
  }, [scenarios, scenarioNames, dealModeFilter]);

  // Single scenario mode state
  const activeEntry = isCompare ? null : proposalEntries[activeTab];
  const activeDealMode = (!activeEntry || activeEntry.error) ? null
    : (preferredDealMode && activeEntry.modes.includes(preferredDealMode) ? preferredDealMode : activeEntry.modes[0]);
  const activeData = activeDealMode ? activeEntry[activeDealMode] : null;

  // NPV-sorted indices for scenario ordering (best NPV first)
  const npvSortedIndices = useMemo(() => {
    const indices = proposalEntries.map((e, i) => {
      if (e.error) return { i, npv: -Infinity };
      const mode = (preferredDealMode && e.modes.includes(preferredDealMode)) ? preferredDealMode : e.modes[0];
      const npv = e[mode]?.npv ?? -Infinity;
      return { i, npv };
    });
    indices.sort((a, b) => b.npv - a.npv); // descending — best first
    return indices.map((x) => x.i);
  }, [proposalEntries, preferredDealMode]);

  // Compare mode: valid entries sorted by NPV (best to worst, left to right)
  const validCompareEntries = useMemo(() => {
    if (!isCompare) return [];
    return npvSortedIndices
      .map((i) => proposalEntries[i])
      .filter((e) => !e.error)
      .map((e) => {
        const mode = (preferredDealMode && e.modes.includes(preferredDealMode)) ? preferredDealMode : e.modes[0];
        return { name: e.name, data: e[mode], mode };
      })
      .filter((e) => e.data);
  }, [isCompare, npvSortedIndices, proposalEntries, preferredDealMode]);

  // Discount rate change handler (placeholder — recalculating NPV live requires the full cashflow model)
  const handleDiscountRateChange = useCallback((key, value) => {
    // For now this updates the display value; full NPV recalculation
    // would require rerunning FinancialAnalysis which is beyond the share view scope.
    // The editable field lets the customer see what rate is assumed.
  }, []);

  // Collect all available deal modes across valid scenarios (for compare mode toggle)
  const allDealModes = useMemo(() => {
    const modeSet = new Set();
    proposalEntries.forEach((e) => { if (!e.error) e.modes.forEach((m) => modeSet.add(m)); });
    return [...modeSet];
  }, [proposalEntries]);

  // Site info from first valid scenario (shared across all scenarios in a project)
  const siteInfo = useMemo(() => {
    const first = proposalEntries.find((e) => !e.error);
    if (!first) return null;
    const data = first.esa || first.capex;
    return data?.siteInfo || null;
  }, [proposalEntries]);

  const dateStr = (createdAt ? new Date(createdAt) : new Date()).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const titleText = customerName
    ? `Energy Proposal for ${customerName}`
    : projectName
      ? `Energy Proposal — ${projectName}`
      : 'Energy Proposal';

  // Pill component for scenario tabs + compare (ordered by NPV)
  const ScenarioPills = () => (
    scenarios.length > 1 && (
      <Box>
        {npvSortedIndices.length > 1 && (
          <Typography variant="caption" sx={{
            color: 'rgba(255,255,255,0.3)',
            fontSize: '0.6rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}>
            NPV
            <ArrowIcon sx={{ fontSize: '0.6rem' }} />
            decreasing
          </Typography>
        )}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
        {npvSortedIndices.map((i, rank) => {
          const name = scenarioNames[i];
          return (
            <React.Fragment key={i}>
              {rank > 0 && rank < npvSortedIndices.length && (
                <ArrowIcon sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.15)', mx: -0.25 }} />
              )}
              <Box
                onClick={() => { setActiveTab(i); setRateOverrides(null); }}
                sx={{
                  px: 1.5, py: 0.5,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: activeTab === i ? vortxColors.gold : 'rgba(255,255,255,0.12)',
                  bgcolor: activeTab === i ? `${vortxColors.gold}15` : 'transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': { borderColor: vortxColors.gold },
                }}
              >
                <Typography variant="body2" sx={{
                  fontWeight: activeTab === i ? 600 : 400,
                  color: activeTab === i ? vortxColors.gold : vortxColors.stone,
                  fontFamily: vortxTypography.body,
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}>
                  {recommendedIndex === i && <StarIcon sx={{ fontSize: '0.8rem', color: vortxColors.gold }} />}
                  {name}
                </Typography>
              </Box>
            </React.Fragment>
          );
        })}
        <Box
          onClick={() => { setActiveTab('compare'); setRateOverrides(null); }}
          sx={{
            px: 1.5, py: 0.5,
            borderRadius: 1.5,
            cursor: 'pointer',
            border: '1px solid',
            borderColor: isCompare ? vortxColors.accent : 'rgba(255,255,255,0.12)',
            bgcolor: isCompare ? `${vortxColors.accent}15` : 'transparent',
            transition: 'all 0.15s ease',
            '&:hover': { borderColor: vortxColors.accent },
            ml: 0.5,
          }}
        >
          <Typography variant="body2" sx={{
            fontWeight: isCompare ? 600 : 400,
            color: isCompare ? vortxColors.accent : vortxColors.stone,
            fontFamily: vortxTypography.body,
            fontSize: '0.75rem',
          }}>
            Compare
          </Typography>
        </Box>
      </Box>
      </Box>
    )
  );

  // Deal mode toggle pills (reused in both modes)
  const DealModeToggle = ({ modes }) => (
    modes.length > 1 && (
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        {modes.map(m => {
          const isActive = isCompare
            ? (preferredDealMode || allDealModes[0]) === m
            : activeDealMode === m;
          return (
            <Box
              key={m}
              onClick={() => setPreferredDealMode(m)}
              sx={{
                px: 1.5, py: 0.5,
                borderRadius: 1.5,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: isActive ? vortxColors.gold : 'rgba(255,255,255,0.12)',
                bgcolor: isActive ? `${vortxColors.gold}15` : 'transparent',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: vortxColors.gold },
              }}
            >
              <Typography variant="body2" sx={{
                fontWeight: isActive ? 600 : 400,
                color: isActive ? vortxColors.gold : vortxColors.stone,
                fontFamily: vortxTypography.body,
                fontSize: '0.75rem',
              }}>
                {m === 'esa' ? 'Energy Service Agreement' : 'Direct Purchase'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    )
  );

  // Compare metrics table — key financial figures side by side
  const CompareMetricsTable = ({ entries }) => {
    if (!entries || entries.length === 0) return null;

    // Build rows: each row is a metric, columns are scenarios
    const firstMode = entries[0]?.data?.mode;

    const metrics = [
      {
        label: 'BAU Utility Cost',
        getValue: (d) => formatCurrency(d.bauAnnualCost),
      },
    ];

    // Payment row — mode-specific, shown before All-in Cost
    if (firstMode === 'esa') {
      metrics.push({ label: 'ESA Payment', getValue: (d) => formatCurrency(d.year1EsaPayment) });
    } else if (firstMode === 'capex') {
      metrics.push({ label: 'Annual O&M', getValue: (d) => formatCurrency(d.year1OM) });
    }

    metrics.push(
      {
        label: 'All-in Cost',
        getValue: (d) => {
          if (d.mode === 'esa') return formatCurrency(d.year1TotalNewCost);
          return formatCurrency((d.optimizedUtilityBill || 0) + (d.year1OM || 0));
        },
        subtitle: `Utility cost + ${firstMode === 'esa' ? 'ESA payments' : 'O&M'}`,
      },
      {
        label: 'Annual Savings',
        getValue: (d) => {
          const sav = d.mode === 'esa' ? d.year1NetSavings : d.year1AnnualSavings;
          return formatCurrency(sav);
        },
        highlight: true,
      },
      {
        label: 'Savings %',
        getValue: (d) => formatPercent(d.savingsPercent, 0),
        highlight: true,
      },
      {
        label: 'Lifetime Savings',
        getValue: (d) => formatCurrency(d.lifetimeSavings),
      },
    );

    // Remaining mode-specific rows
    if (firstMode === 'esa') {
      metrics.push(
        { label: 'Term', getValue: (d) => d.esaTerms?.term ? `${d.esaTerms.term} yrs` : '—' },
      );
    } else if (firstMode === 'capex') {
      metrics.push(
        { label: 'Net Cost', getValue: (d) => formatCurrency(d.capexTerms?.netCost) },
        { label: 'Payback', getValue: (d) => d.capexTerms?.paybackYears != null ? `${d.capexTerms.paybackYears.toFixed(1)} yrs` : '—' },
      );
    }

    // VPP Revenue — only if any entry has VPP enabled
    if (entries.some(e => e.data?.vpp?.included && e.data.vpp.annualRevenue > 0)) {
      metrics.push({
        label: 'VPP Revenue (Annual)',
        getValue: (d) => d.vpp?.included && d.vpp.annualRevenue > 0 ? formatCurrency(d.vpp.annualRevenue) : '—',
        highlight: true,
      });
    }

    // NPV — always last, highlighted
    metrics.push({
      label: 'NPV',
      getValue: (d) => d.npv != null ? formatCurrency(d.npv) : '—',
      highlight: true,
      bold: true,
    });

    return (
      <Box
        sx={{
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.08)',
          bgcolor: 'rgba(255,255,255,0.03)',
          overflow: 'hidden',
        }}
      >
        {/* Sort indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 2, pt: 1 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.6rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Best NPV
            <ArrowIcon sx={{ fontSize: '0.75rem' }} />
            Least NPV
          </Typography>
        </Box>

        {/* Column headers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `140px repeat(${entries.length}, 1fr)`,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            px: 2,
            py: 1,
          }}
        >
          <Box />
          {entries.map((e, i) => (
            <Typography
              key={i}
              variant="caption"
              sx={{
                color: recommendedIndex === i ? vortxColors.gold : vortxColors.stone,
                fontWeight: 600,
                fontFamily: vortxTypography.body,
                fontSize: '0.7rem',
                textAlign: 'right',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
              }}
            >
              {recommendedIndex === i && <StarIcon sx={{ fontSize: '0.7rem' }} />}
              {e.name}
            </Typography>
          ))}
        </Box>

        {/* Metric rows */}
        {metrics.map((metric, mi) => (
          <Box
            key={metric.label}
            sx={{
              display: 'grid',
              gridTemplateColumns: `140px repeat(${entries.length}, 1fr)`,
              px: 2,
              py: 1.25,
              borderTop: mi > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none',
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ color: vortxColors.stone, fontSize: '0.8rem' }}>
                {metric.label}
              </Typography>
              {metric.subtitle && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.6rem' }}>
                  {metric.subtitle}
                </Typography>
              )}
            </Box>
            {entries.map((e, ei) => (
              <Typography
                key={ei}
                sx={{
                  fontFamily: vortxTypography.mono,
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  color: metric.highlight ? vortxColors.gold : vortxColors.white,
                  textAlign: 'right',
                }}
              >
                {metric.getValue(e.data)}
              </Typography>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 } }} className="proposal-view">
      {/* Print styles */}
      <style>{`
        @media print {
          body { background: #1a1a1a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .proposal-view { padding: 0 !important; }
          .proposal-view * { color-adjust: exact; -webkit-print-color-adjust: exact; }
          .no-print { display: none !important; }
          .MuiAppBar-root, .MuiToolbar-root, header { display: none !important; }
          .MuiContainer-root { max-width: 100% !important; padding: 20px !important; }
        }
      `}</style>

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            position: 'relative',
          }}
        >
          {/* PDF download button */}
          <IconButton
            onClick={() => window.print()}
            className="no-print"
            title="Download PDF"
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              color: vortxColors.stone,
              '&:hover': { color: vortxColors.gold },
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <Typography
            component="span"
            sx={{
              fontFamily: vortxTypography.heading,
              fontWeight: 700,
              fontSize: '1.25rem',
              color: vortxColors.gold,
              letterSpacing: '0.1em',
            }}
          >
            VORTX
          </Typography>
          <Typography
            component="span"
            sx={{
              fontFamily: vortxTypography.heading,
              fontWeight: 300,
              fontSize: '1.25rem',
              color: vortxColors.stone,
              letterSpacing: '0.1em',
            }}
          >
            FLUX
          </Typography>
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontFamily: vortxTypography.heading,
            fontWeight: 700,
            color: vortxColors.white,
            fontSize: { xs: '1.5rem', sm: '2rem' },
          }}
        >
          {titleText}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: vortxColors.stone, mt: 0.5 }}
        >
          Prepared {dateStr}
        </Typography>

        {/* Site info */}
        {siteInfo && (siteInfo.city || siteInfo.state || siteInfo.utilityName || siteInfo.tariffName) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1.5,
            }}
          >
            {(siteInfo.city || siteInfo.state) && (
              <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                {[siteInfo.city, siteInfo.state].filter(Boolean).join(', ')}
              </Typography>
            )}
            {(siteInfo.city || siteInfo.state) && siteInfo.utilityName && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>|</Typography>
            )}
            {siteInfo.utilityName && (
              <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                {siteInfo.utilityName}
              </Typography>
            )}
            {siteInfo.utilityName && siteInfo.tariffName && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>|</Typography>
            )}
            {siteInfo.tariffName && (
              <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                {siteInfo.tariffName}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 4 }} />

      {/* Compare mode */}
      {isCompare ? (
        <>
          {/* System Overview — grouped by scenario */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography
              variant="overline"
              sx={{ color: vortxColors.stone, fontWeight: 600, letterSpacing: '0.1em' }}
            >
              Proposed System
            </Typography>
            <ScenarioPills />
          </Box>
          <ProposalSystemOverview
            entries={validCompareEntries.map((e) => ({ name: e.name, system: e.data.system }))}
            hideTitle
          />

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

          {/* Compare metrics table */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography
              variant="overline"
              sx={{ color: vortxColors.stone, fontWeight: 600, letterSpacing: '0.1em' }}
            >
              Financial Comparison
            </Typography>
            <DealModeToggle modes={allDealModes} />
          </Box>
          <CompareMetricsTable entries={validCompareEntries} />

          {/* Recommendation callout */}
          {recommendedIndex != null && recommendedIndex >= 0 && recommendationText && (
            <Box sx={{
              mt: 3, p: 2.5, borderRadius: 2,
              border: `1px solid ${vortxColors.gold}30`,
              bgcolor: `${vortxColors.gold}08`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <StarIcon sx={{ fontSize: '1rem', color: vortxColors.gold }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: vortxColors.gold, fontSize: '0.8rem' }}>
                  Our Recommendation
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: vortxColors.white, lineHeight: 1.6, fontSize: '0.85rem' }}>
                {recommendationText}
              </Typography>
            </Box>
          )}

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

          {/* Savings Timeline — overlaid lines */}
          <ProposalSavingsTimeline
            entries={validCompareEntries.map((e) => ({ name: e.name, data: e.data }))}
          />
        </>
      ) : !activeEntry || activeEntry.error ? (
        <Box sx={{ mb: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Unable to generate proposal: {activeEntry?.error || 'No scenario data available'}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This scenario may not have optimization results or deal analysis
            configured. Please re-run the optimization and configure deal terms.
          </Typography>
        </Box>
      ) : (
        <>
          {/* System Overview — scenario tabs inline with section title */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography
              variant="overline"
              sx={{ color: vortxColors.stone, fontWeight: 600, letterSpacing: '0.1em' }}
            >
              Proposed System
            </Typography>
            <ScenarioPills />
          </Box>
          <ProposalSystemOverview system={activeData.system} hideTitle />

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

          {/* Hero — savings headline + cost bars */}
          <ProposalHero data={activeData} />

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

          {/* Performance Summary */}
          <ProposalPerformance scenario={scenarios[activeTab]} />

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

          {/* Terms — deal mode toggle inline with section title */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography
              variant="overline"
              sx={{ color: vortxColors.stone, fontWeight: 600, letterSpacing: '0.1em' }}
            >
              Financials & Commercial
            </Typography>
            <DealModeToggle modes={activeEntry.modes} />
          </Box>
          <ProposalTerms data={activeData} hideTitle onDiscountRateChange={handleDiscountRateChange} />

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

          {/* Monthly Performance */}
          <ProposalMonthlyPerformance scenario={scenarios[activeTab]} />

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

          {/* Financial Assumptions — interactive escalators */}
          <ProposalAssumptions data={activeData} onRatesChange={setRateOverrides} />

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

          {/* Year-by-Year Cashflows */}
          <ProposalCashflowTable data={activeData} rateOverrides={rateOverrides} />

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

          {/* Dispatch Chart */}
          <ProposalDispatch scenario={scenarios[activeTab]} />

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

          {/* Savings Timeline — cumulative chart */}
          <ProposalSavingsTimeline data={activeData} />
        </>
      )}

      {/* Disclaimer */}
      <Box sx={{ mt: 6, pt: 3, borderTop: `1px solid rgba(255,255,255,0.08)`, textAlign: 'center' }}>
        <Typography
          variant="caption"
          sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem' }}
        >
          This proposal is for informational purposes only and does not
          constitute a binding offer. Actual savings may vary based on energy
          usage, utility rates, and system performance.
        </Typography>
      </Box>
    </Container>
  );
}
